import axios from "axios";
import {
  getManusConfig,
  debugLog,
  generateRequestId,
} from "../utils/manusConfig";
import { getNextKey, getKeyById } from "../utils/manusKeyManager";

const MANUS_TIMEOUT_ERROR = "MANUS_TASK_TIMEOUT";
const MANUS_API_KEY_MISSING = "MANUS_API_KEY_MISSING";

const buildAxiosClient = async (options = {}) => {
  const config = getManusConfig();
  const { keyId } = options; // Accept keyId to use specific key

  if (!config.apiUrl) {
    throw new Error(
      "Manus API URL is not configured (REACT_APP_MANUS_API_URL)"
    );
  }

  // Build headers
  const headers = {
    "Content-Type": "application/json",
  };

  // Get API key using round-robin key manager
  let apiKeyToUse = null;
  let keySource = "none";
  let usedKeyId = null;

  if (!config.useProxy) {
    try {
      // ✅ Nếu có keyId được truyền vào, ưu tiên sử dụng key đó
      if (keyId) {
        const keyData = await getKeyById(keyId);
        
        if (keyData && keyData.key && keyData.enabled) {
          apiKeyToUse = keyData.key;
          usedKeyId = keyData.id;
          keySource = `specified (${keyId})`;
          debugLog("Using specified Manus API key", {
            keyId: usedKeyId,
            source: keySource,
          });
        } else if (keyId === "env_key") {
          // Special case: env_key is always valid
          apiKeyToUse = config.apiKey;
          usedKeyId = "env_key";
          keySource = "specified (env_key)";
          debugLog("Using env_key as specified", { keyId: "env_key" });
        } else {
          debugLog("Specified key not found or disabled, falling back to round-robin", { keyId });
        }
      }

      // Nếu không có keyId hoặc key không hợp lệ, dùng round-robin
      if (!apiKeyToUse) {
        const keyData = await getNextKey();

        if (keyData && keyData.key) {
          apiKeyToUse = keyData.key;
          usedKeyId = keyData.keyId;
          keySource = `${keyData.source} (${keyData.keyId})`;
          debugLog("Using Manus API key from manager", {
            keyId: keyData.keyId,
            source: keyData.source,
          });
        } else {
          // Fallback to env key if key manager returns null
          apiKeyToUse = config.apiKey;
          usedKeyId = "env_key";
          keySource = "environment (fallback)";
          debugLog("No keys from manager, using env key as fallback");
        }
      }
    } catch (error) {
      console.warn("Error getting key from manager, using env key:", error);
      apiKeyToUse = config.apiKey;
      usedKeyId = "env_key";
      keySource = "environment (error fallback)";
    }
  } else {
    // ✅ Khi dùng proxy, luôn gán usedKeyId dù không dùng header API_KEY
    // Điều này đảm bảo keyId được trả về cho client sử dụng trong các request tiếp theo
    usedKeyId = keyId || "unknown";
    debugLog("Using proxy mode", { usedKeyId, useProxy: true });
  }

  // Final validation (only needed when NOT using proxy)
  if (!config.useProxy && !apiKeyToUse) {
    const error = new Error(
      "Manus API key is missing. Please set REACT_APP_MANUS_API_KEY or add keys via Admin panel."
    );
    error.code = MANUS_API_KEY_MISSING;
    throw error;
  }

  // Add API_KEY header when NOT using proxy
  if (!config.useProxy && apiKeyToUse) {
    headers["API_KEY"] = apiKeyToUse;
  }

  debugLog("Building Manus client", {
    baseURL: config.apiUrl,
    useProxy: config.useProxy,
    keySource,
    hasApiKey: !!apiKeyToUse,
  });

  return {
    client: axios.create({
      baseURL: config.apiUrl.replace(/\/+$/, ""),
      headers,
      timeout: config.requestTimeout,
    }),
    config,
    keyId: usedKeyId, // Return keyId so caller knows which key was used
  };
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const createTask = async ({
  prompt,
  attachments = [],
  taskMode,
  agentProfile,
  mode,
  locale,
  createShareableLink,
  hideInTaskList,
  taskId,
  connectors,
} = {}) => {
  if (!prompt) {
    throw new Error("createTask requires a prompt");
  }

  const { client, config, keyId } = await buildAxiosClient();

  // Simplified payload - only required fields
  const payload = {
    prompt,
  };

  // Determine effective agent profile (accept both agentProfile and mode for compatibility)
  const effectiveAgentProfile = agentProfile || mode || config.agentProfile;
  
  if (effectiveAgentProfile) {
    payload.agentProfile = effectiveAgentProfile; // ✅ Use correct field name per Manus API spec
  }

  // Add optional fields only if provided
  if (attachments && attachments.length > 0) {
    payload.attachments = attachments;
  }

  if (taskId) {
    payload.taskId = taskId;
  }

  if (connectors && connectors.length) {
    payload.connectors = connectors;
  }

  debugLog("Creating Manus task", payload);

  // ✅ FIX: Always use "/tasks" endpoint
  // Proxy mode: baseURL="/api/manus" + "/tasks" = /api/manus/tasks
  // Direct mode: baseURL="https://api.manus.ai/v1" + "/tasks" = /v1/tasks
  const endpoint = "/tasks";
  const response = await client.post(endpoint, payload);
  
  // ✅ Đảm bảo _keyId luôn được gán - điều này quan trọng cho các request poll tiếp theo
  const resultKeyId = response.data._keyId || keyId;
  
  if (!resultKeyId) {
    console.warn(
      "⚠️ [Manus] Warning: _keyId is not available after task creation",
      { response: response.data }
    );
  }

  debugLog("Task created with keyId", {
    taskId: response.data.task_id,
    keyId: resultKeyId,
    source: response.data._keyId ? "from proxy" : "from client",
  });

  return {
    ...response.data,
    _keyId: resultKeyId, // Use keyId from response if available (proxy mode), otherwise from client
  };
};

export const getTask = async (taskId, keyId = null) => {
  if (!taskId) {
    throw new Error("getTask requires taskId");
  }

  const { client, config } = await buildAxiosClient({ keyId });

  // Use query param for proxy, path param for direct API
  // Include keyId in query params if available
  if (config.useProxy) {
    const params = { taskId };
    if (keyId) params.keyId = keyId;
    
    // ✅ Call /tasks with taskId query param for proxy mode
    // Client sends: GET /api/manus/tasks?taskId=xxx
    const response = await client.get("/tasks", { params });
    return response.data;
  } else {
    const params = keyId ? { keyId } : {};
    const response = await client.get(`/tasks/${taskId}`, { params });
    return response.data;
  }
};

export const listTasks = async (params = {}) => {
  const { client, config } = await buildAxiosClient();
  // ✅ Always use /tasks endpoint
  const endpoint = "/tasks";
  const response = await client.get(endpoint, { params });
  return response.data;
};

export const updateTask = async (taskId, data = {}) => {
  if (!taskId) {
    throw new Error("updateTask requires taskId");
  }

  const { client, config } = await buildAxiosClient();
  // ✅ Use /tasks with query param for proxy, path param for direct API
  const endpoint = config.useProxy ? "/tasks" : `/tasks/${taskId}`;
  const params = config.useProxy ? { taskId } : {};
  const response = await client.put(endpoint, data, { params });
  return response.data;
};

export const deleteTask = async (taskId) => {
  if (!taskId) {
    throw new Error("deleteTask requires taskId");
  }

  const { client, config } = await buildAxiosClient();
  // ✅ Use /tasks with query param for proxy, path param for direct API
  const endpoint = config.useProxy ? "/tasks" : `/tasks/${taskId}`;
  const params = config.useProxy ? { taskId } : {};
  const response = await client.delete(endpoint, { params });
  return response.data;
};

export const createFileRecord = async ({ filename }) => {
  if (!filename) {
    throw new Error("createFileRecord requires filename");
  }

  const { client, config } = await buildAxiosClient();
  // ✅ Always use /files endpoint
  const endpoint = "/files";
  const params = config.useProxy ? { filename } : {};
  const response = await client.post(endpoint, config.useProxy ? {} : { filename }, { params });
  return response.data;
};

export const listFiles = async () => {
  const { client, config } = await buildAxiosClient();
  // ✅ Always use /files endpoint
  const endpoint = "/files";
  const response = await client.get(endpoint);
  return response.data;
};

export const getFile = async (fileId) => {
  if (!fileId) {
    throw new Error("getFile requires fileId");
  }

  const { client, config } = await buildAxiosClient();
  // ✅ Use /files with query param for proxy, path param for direct API
  const endpoint = config.useProxy ? "/files" : `/files/${fileId}`;
  const params = config.useProxy ? { fileId } : {};
  const response = await client.get(endpoint, { params });
  return response.data;
};

export const deleteFile = async (fileId) => {
  if (!fileId) {
    throw new Error("deleteFile requires fileId");
  }

  const { client, config } = await buildAxiosClient();
  // ✅ Use /files with query param for proxy, path param for direct API
  const endpoint = config.useProxy ? "/files" : `/files/${fileId}`;
  const params = config.useProxy ? { fileId } : {};
  const response = await client.delete(endpoint, { params });
  return response.data;
};

export const uploadFileContent = async (uploadUrl, data, mimeType) => {
  if (!uploadUrl) {
    throw new Error("uploadFileContent requires uploadUrl");
  }
  if (!data) {
    throw new Error("uploadFileContent requires data to upload");
  }

  const response = await fetch(uploadUrl, {
    method: "PUT",
    headers: {
      "Content-Type": mimeType || "application/octet-stream",
    },
    body: data,
  });

  if (!response.ok) {
    throw new Error(
      `Failed to upload file to Manus storage (status ${response.status})`
    );
  }

  return true;
};

export const pollTaskUntilComplete = async (
  taskId,
  { intervalMs, timeoutMs, onStatus, keyId } = {}
) => {
  // ✅ Validate keyId - warn if not provided since it's critical for proper polling
  if (!keyId) {
    console.warn(
      "⚠️ [Manus] Warning: pollTaskUntilComplete called without keyId. " +
      "This may cause 404 errors if using round-robin key rotation. " +
      "Task may not be found if created with a different API key.",
      { taskId }
    );
  }

  const { client, config } = await buildAxiosClient({ keyId });

  const effectiveInterval =
    typeof intervalMs === "number" && intervalMs > 0
      ? intervalMs
      : config.pollingIntervalMs || 4000;

  const effectiveTimeout =
    typeof timeoutMs === "number" && timeoutMs > 0
      ? timeoutMs
      : config.pollingTimeoutMs || 600000;

  const startedAt = Date.now();
  let attempt = 0;
  let consecutiveNotFoundCount = 0;
  const MAX_NOT_FOUND_RETRIES = 5; // Allow up to 5 consecutive 404s before giving up

  // eslint-disable-next-line no-constant-condition
  while (true) {
    attempt += 1;
    let response;

    try {
      // Use query param for proxy, path param for direct API
      // Include keyId in query params if available (critical for maintaining same API key)
      if (config.useProxy) {
        const params = { taskId };
        if (keyId) {
          params.keyId = keyId;
        } else {
          console.warn(
            "⚠️ [Manus] Polling without keyId using proxy - will use round-robin rotation",
            { taskId, attempt }
          );
        }

        // 🐛 DEBUG: Log request trước khi gọi
        console.log("🐛 [Manus Client] GET /api/manus/tasks with params:", {
          url: "/tasks",
          params,
          taskId,
          keyId,
          attempt,
        });

        // ✅ Call /tasks with taskId query param
        // Client sends: GET /api/manus/tasks?taskId=xxx
        response = await client.get("/tasks", { params });
      } else {
        const params = keyId ? { keyId } : {};

        // 🐛 DEBUG: Log request trước khi gọi
        console.log("🐛 [Manus Client] GET /tasks/{taskId} with params:", {
          url: `/tasks/${taskId}`,
          params,
          taskId,
          keyId,
          attempt,
        });

        response = await client.get(`/tasks/${taskId}`, { params });
      }

      // Reset 404 counter on successful response
      consecutiveNotFoundCount = 0;

      const task = response.data;
      const status = task?.status;

      debugLog("Polling Manus task", {
        taskId,
        status,
        attempt,
        keyId,
        hasKeyId: !!keyId,
      });

      if (onStatus) {
        onStatus({
          attempt,
          task,
          status,
          elapsedMs: Date.now() - startedAt,
        });
      }

      if (status === "completed") {
        return task;
      }

      if (status === "failed") {
        const error = new Error(task?.error || "Manus task failed");
        error.task = task;
        throw error;
      }

      if (Date.now() - startedAt > effectiveTimeout) {
        const timeoutError = new Error(
          "Polling Manus task timed out before completion"
        );
        timeoutError.code = MANUS_TIMEOUT_ERROR;
        timeoutError.task = task;
        throw timeoutError;
      }

      await sleep(effectiveInterval);
    } catch (error) {
      // Handle 404 errors gracefully - task might not be indexed yet
      if (error.response && error.response.status === 404) {
        consecutiveNotFoundCount++;
        
        debugLog("Task not found (404), might not be indexed yet", {
          taskId,
          keyId,
          hasKeyId: !!keyId,
          attempt,
          consecutiveNotFoundCount,
        });

        // If we've seen too many 404s and we're past the initial delay period,
        // the task probably doesn't exist
        if (consecutiveNotFoundCount >= MAX_NOT_FOUND_RETRIES && 
            Date.now() - startedAt > 20000) { // After 20 seconds
          const errorMessage = keyId
            ? `Task ${taskId} not found after ${MAX_NOT_FOUND_RETRIES} attempts with keyId '${keyId}'. ` +
              `Task may not exist or was created with a different API key.`
            : `Task ${taskId} not found after ${MAX_NOT_FOUND_RETRIES} attempts. ` +
              `No keyId was provided - task may have been created with a specific API key. ` +
              `Please provide the correct keyId.`;
          
          const notFoundError = new Error(errorMessage);
          notFoundError.code = "TASK_NOT_FOUND";
          notFoundError.taskId = taskId;
          notFoundError.keyId = keyId;
          notFoundError.hasKeyId = !!keyId;
          throw notFoundError;
        }

        // Wait longer before retry on 404
        await sleep(Math.min(effectiveInterval * 1.5, 8000));
        continue;
      }

      // For other errors, rethrow immediately
      throw error;
    }
  }
};

export const extractTaskOutputText = (task) => {
  if (!task?.output) {
    return "";
  }

  const messages = Array.isArray(task.output) ? task.output : [];
  const parts = [];

  messages.forEach((message) => {
    const contents = Array.isArray(message?.content) ? message.content : [];

    contents.forEach((content) => {
      if (content?.type === "output_text" && content.text) {
        parts.push(content.text.trim());
      }
    });
  });

  return parts.join("\n\n").trim();
};

/**
 * Extract output files từ task (để tải file markdown/pdf từ Manus)
 * @param {object} task - Manus task object
 * @returns {Array} - Array of file objects với fileUrl, fileName, mimeType
 */
export const extractTaskOutputFiles = (task) => {
  if (!task?.output) {
    return [];
  }

  const messages = Array.isArray(task.output) ? task.output : [];
  const files = [];

  messages.forEach((message) => {
    const contents = Array.isArray(message?.content) ? message.content : [];

    contents.forEach((content) => {
      if (content?.type === "output_file") {
        files.push({
          fileUrl: content.fileUrl,
          fileName: content.fileName,
          mimeType: content.mimeType,
        });
      }
    });
  });

  return files;
};

export const parseTaskOutputJson = (task) => {
  const rawText = extractTaskOutputText(task);
  if (!rawText) {
    return {
      rawText,
      data: null,
      error: new Error("Task output is empty"),
    };
  }

  const sanitized = rawText
    .replace(/^```json\s*/i, "")
    .replace(/^```\s*/i, "")
    .replace(/```$/i, "")
    .trim();

  const firstBrace = sanitized.indexOf("{");
  const lastBrace = sanitized.lastIndexOf("}");

  if (firstBrace === -1 || lastBrace === -1 || lastBrace <= firstBrace) {
    return { rawText, data: null, error: new Error("No JSON object found") };
  }

  const candidate = sanitized.slice(firstBrace, lastBrace + 1);

  try {
    const parsed = JSON.parse(candidate);
    return { rawText, data: parsed, error: null };
  } catch (error) {
    return { rawText, data: null, error };
  }
};

export const buildTaskMetadata = ({ requestId, analysisType, userId }) => {
  const metadata = {
    requestId: requestId || generateRequestId("manus-task"),
    analysisType: analysisType || "price_analysis",
  };

  if (userId) {
    metadata.userId = userId;
  }

  return metadata;
};

export const ManusApiErrors = {
  TIMEOUT: MANUS_TIMEOUT_ERROR,
  API_KEY_MISSING: MANUS_API_KEY_MISSING,
};

const manusApi = {
  createTask,
  getTask,
  listTasks,
  updateTask,
  deleteTask,
  createFileRecord,
  listFiles,
  getFile,
  deleteFile,
  uploadFileContent,
  pollTaskUntilComplete,
  extractTaskOutputText,
  extractTaskOutputFiles,
  parseTaskOutputJson,
  buildTaskMetadata,
  ManusApiErrors,
};

export default manusApi;
